import { Component, Inject, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { take } from 'rxjs/operators';

@Component({
  selector: 'app-click-details-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule],
  templateUrl: './click-details-dialog.component.html',
  styleUrls: ['./click-details-dialog.component.css'],
})
export class ClickDetailsDialogComponent implements OnInit, OnDestroy {
  activeTab: 'overview' | 'media' | 'screenshot' | 'location' | 'device' | 'fingerprint' = 'overview';
  private dataUpdatesSubject: Subject<any> | null = null;

  constructor(
    public dialogRef: MatDialogRef<ClickDetailsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    const dataUpdates$ = this.data?.dataUpdates$ as Subject<any> | undefined;
    if (dataUpdates$) {
      this.dataUpdatesSubject = dataUpdates$;
      dataUpdates$.pipe(take(1)).subscribe((updated) => {
        this.data = updated;
        this.cdr.markForCheck();
      });
    }
  }

  ngOnDestroy(): void {
    this.dataUpdatesSubject?.complete();
  }

  close(): void {
    this.dialogRef.close();
  }

  setActiveTab(tab: typeof this.activeTab): void {
    this.activeTab = tab;
  }

  getLocationHistory(): Array<{
    latitude: number | null;
    longitude: number | null;
    accuracy?: number;
    altitude?: number;
    speed?: number;
    heading?: number;
    timestamp: string;
  }> {
    const raw = this.data?.locationHistory;
    return Array.isArray(raw) ? raw : [];
  }

  getPrimaryGpsPoint(): {
    latitude: number | null;
    longitude: number | null;
    accuracy?: number;
    altitude?: number;
    speed?: number;
    heading?: number;
    timestamp: string;
  } | null {
    const history = this.getLocationHistory();
    if (history.length > 0) {
      return history[history.length - 1];
    }

    // Fallback: initial /api/track stores gpsLatitude/gpsLongitude even when
    // locationHistory was never seeded (e.g. older clicks, no continuous updates).
    const lat = this.data?.gpsLatitude ?? this.data?.latitude;
    const lng = this.data?.gpsLongitude ?? this.data?.longitude;
    if (typeof lat !== 'number' || typeof lng !== 'number') {
      return null;
    }

    const accuracyRaw = this.data?.gpsAccuracy;
    const accuracy =
      typeof accuracyRaw === 'number'
        ? accuracyRaw
        : typeof accuracyRaw === 'string' && accuracyRaw.trim() !== '' && !Number.isNaN(Number(accuracyRaw))
          ? Number(accuracyRaw)
          : undefined;

    return {
      latitude: lat,
      longitude: lng,
      accuracy,
      altitude: typeof this.data?.gpsAltitude === 'number' ? this.data.gpsAltitude : undefined,
      speed: typeof this.data?.gpsSpeed === 'number' ? this.data.gpsSpeed : undefined,
      heading: typeof this.data?.gpsHeading === 'number' ? this.data.gpsHeading : undefined,
      timestamp: this.data?.timestamp || this.data?.raw?.timestamp || new Date().toISOString(),
    };
  }

  hasGpsHistory(): boolean {
    return this.getPrimaryGpsPoint() != null;
  }

  formatLocationTimestamp(ts: string): string {
    try {
      const d = new Date(ts);
      return d.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
      });
    } catch {
      return ts;
    }
  }

  /** GPS coordinates from latest locationHistory point */
  getGpsCoords(): string {
    const latest = this.getPrimaryGpsPoint();
    const lat = latest?.latitude;
    const lon = latest?.longitude;
    if (typeof lat !== 'number' || typeof lon !== 'number') return '';
    return `${lat},${lon}`;
  }

  /** IP geolocation coordinates (from IP address lookup) */
  getIpCoords(): string {
    const lat = this.data.ipLatitude ?? 22.5643;
    const lon = this.data.ipLongitude ?? 88.3693;
    return `${lat},${lon}`;
  }

  openGoogleMaps(): void {
    const coords = this.getGpsCoords();
    if (!coords) return;
    window.open(`https://www.google.com/maps?q=${coords}`, '_blank');
  }

  openGoogleMapsForPoint(lat: number | null, lon: number | null): void {
    if (typeof lat !== 'number' || typeof lon !== 'number') return;
    window.open(`https://www.google.com/maps?q=${lat},${lon}`, '_blank');
  }

  openIpLocation(): void {
    const coords = this.getIpCoords();
    window.open(`https://www.google.com/maps?q=${coords}`, '_blank');
  }

  downloadCapture(url: string, index: number): void {
    const a = document.createElement('a');
    a.href = url;
    a.download = `camera-capture-${index + 1}.jpg`;
    a.target = '_blank';
    a.click();
  }

  downloadScreenshot(): void {
    const url = this.getScreenshot();
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = 'screenshot.jpg';
    a.target = '_blank';
    a.click();
  }

  getScreenshot(): string | null {
    return this.data.screenshot ?? null;
  }

  getCameraCaptures(): string[] {
    return this.data.cameraCaptures ?? [];
  }
}
